import { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { devNote } from '@xingternal/malt-util';

/**
 * Use portals to render content into a different part of the DOM. If you need to break out of the confinements of your code structure, use this component to put the content into a different part of the app.
 *
 * Please remember: The `children` of this component are still connected to the part of your app you render the Portal in. See the preview as an example where the counter and the count up button are not in the same place but still respond to each other. Take care of not confusing your users when they take action in one place and a different part of the interface updates accordingly.
 */

class Portal extends Component {
  state = {
    mounted: false,
  };

  componentDidMount() {
    this.portalContainer =
      document.getElementById(this.props.containerId) ||
      document.createElement('div');
    // eslint-disable-next-line
    this.setState({
      mounted: true,
    });
    if (!this.props.containerId) {
      document.body.appendChild(this.portalContainer);
    }
  }

  componentWillUnmount() {
    if (!this.props.containerId) {
      document.body.removeChild(this.portalContainer);
    }
  }

  render() {
    if (!this.state.mounted) {
      return null;
    }
    if (
      this.props.containerId &&
      !document.getElementById(this.props.containerId)
    ) {
      devNote.warn(
        'You passed a containerId to malt-portal but we could not find the container in your DOM. Nothing is rendered now. Please check why the container is not present.'
      );
      return null;
    }
    return ReactDOM.createPortal(this.props.children, this.portalContainer);
  }
}

Portal.propTypes = {
  /** Contents of Portal */
  children: PropTypes.node,
  /** ID of the container the children should be rendered into. Make sure to have the container present in your DOM, otherwise no portal will be rendered if you pass this to the component. If you do not pass a containerId, the portal will be rendered in a `div` that is appended to the document `body` */
  containerId: PropTypes.string,
};

Portal.displayName = 'Portal';

export default Portal;
